(() => {
  const COUNTRY_KEY = 'ovilixaDeliveryCountry';
  const PROVINCE_KEY = 'ovilixaDeliveryProvince';
  const CHANGE_EVENT = 'ovilixa:delivery-destination-change';
  let countriesPromise;

  const readStorage = (key) => {
    try { return sessionStorage.getItem(key) || ''; } catch (_) { return ''; }
  };
  const writeStorage = (key, value) => {
    try { sessionStorage.setItem(key, value); } catch (_) { /* Estimates still work without storage. */ }
  };
  const loadCountries = () => {
    if (!countriesPromise) {
      countriesPromise = fetch(`${window.Shopify?.routes?.root || '/'}services/countries.json`)
        .then((response) => response.ok ? response.json() : [])
        .catch(() => []);
    }
    return countriesPromise;
  };
  const normalizeCountries = (payload) => (Array.isArray(payload) ? payload : Object.values(payload || {})).map((entry) => ({
    code: String(entry.code || entry.iso_code || entry.isoCode || '').toUpperCase(),
    provinces: Array.isArray(entry.provinces) ? entry.provinces.map((province) => ({
      code: String(province.code || province.iso_code || province.isoCode || '').toUpperCase(),
      name: province.name || province.code || '',
    })) : [],
  }));
  const escape = (value) => {
    const node = document.createElement('span');
    node.textContent = String(value);
    return node.innerHTML;
  };

  class OvilixaProductDelivery extends HTMLElement {
    connectedCallback() {
      this.summary = this.querySelector('[data-delivery-summary]');
      this.toggle = this.querySelector('[data-delivery-toggle]');
      this.toggleLabel = this.querySelector('[data-delivery-toggle-label]');
      this.chooser = this.querySelector('[data-delivery-chooser]');
      this.countrySelect = this.querySelector('[data-delivery-country]');
      this.provinceSelect = this.querySelector('[data-delivery-province]');
      this.provinceField = this.querySelector('[data-province-field]');
      this.hint = this.querySelector('[data-destination-hint]');
      if (!this.summary || !this.countrySelect || !this.provinceSelect) return;

      const codes = [...this.countrySelect.options].map((option) => option.value);
      const fallback = (this.dataset.defaultCountry || window.OvilixaDeliveryData?.defaultCountry || 'US').toUpperCase();
      if (window.OvilixaDeliveryLocalization?.reconcileLegacySelection(fallback, codes)) return;
      const marketCountry = window.OvilixaDeliveryLocalization?.marketCountry(fallback) || fallback;
      if (codes.includes(marketCountry)) this.countrySelect.value = marketCountry;

      this.toggle?.addEventListener('click', () => {
        const opening = this.chooser.hidden;
        this.chooser.hidden = !opening;
        this.toggle.setAttribute('aria-expanded', String(opening));
        if (this.toggleLabel) this.toggleLabel.textContent = opening ? 'Close destination selector' : 'Change destination';
        if (opening) this.countrySelect.focus();
      });
      this.countrySelect.addEventListener('change', () => {
        writeStorage(COUNTRY_KEY, this.countrySelect.value);
        writeStorage(PROVINCE_KEY, '');
        this.summary.innerHTML = '<strong>Updating your destination…</strong><span>Availability, prices, and delivery will refresh together.</span>';
        this.setAttribute('aria-busy', 'true');
        if (window.OvilixaDeliveryLocalization?.submitCountry(this.countrySelect.value)) return;
        this.renderProvinces('');
        this.broadcast();
      });
      this.provinceSelect.addEventListener('change', () => {
        writeStorage(PROVINCE_KEY, this.provinceSelect.value);
        this.broadcast();
      });
      this.onDestinationChange = (event) => this.render(event.detail || {});
      document.addEventListener(CHANGE_EVENT, this.onDestinationChange);
      document.addEventListener('ovilixa:delivery-data-ready', this.onDataReady = () => this.initialize());

      loadCountries().then((payload) => {
        if (!this.isConnected) return;
        this.countries = normalizeCountries(payload);
        this.renderProvinces(readStorage(PROVINCE_KEY));
        this.broadcast();
      });
      this.initialize();
      this.subscribeToVariantChanges();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.onDestinationChange);
      document.removeEventListener('ovilixa:delivery-data-ready', this.onDataReady);
      if (this.unsubscribeVariant) this.unsubscribeVariant();
    }

    subscribeToVariantChanges() {
      const productInfo = this.closest('product-info');
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
          if (data?.sectionId && data.sectionId !== productInfo?.dataset.section) return;
          if (data?.variant?.id) this.dataset.variantId = data.variant.id;
          this.initialize();
        });
      } else {
        productInfo?.addEventListener('change', () => requestAnimationFrame(() => {
          const id = productInfo.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
          if (id) this.dataset.variantId = id;
          this.initialize();
        }));
      }
    }

    initialize() {
      this.renderProvinces(readStorage(PROVINCE_KEY));
      this.broadcast();
    }

    mapping() {
      const data = window.OvilixaDeliveryData;
      const mapping = data?.variantMap?.[this.dataset.variantId];
      return { data, mapping, estimate: mapping ? data?.estimates?.[mapping.estimate] : null };
    }

    renderProvinces(selectedCode) {
      const countryCode = this.countrySelect.value.toUpperCase();
      const country = (this.countries || []).find((entry) => entry.code === countryCode);
      const destination = this.mapping().estimate?.destinations?.[countryCode];
      const provinces = country?.provinces || [];
      const canSelect = Boolean(destination?.requiresProvince && provinces.length && destination.provinces);
      this.provinceSelect.innerHTML = '<option value="">Select state / province</option>';
      provinces.forEach((province) => this.provinceSelect.add(new Option(province.name, province.code)));
      this.provinceField.hidden = !canSelect;
      this.provinceSelect.disabled = !canSelect;
      if (canSelect && provinces.some((province) => province.code === selectedCode)) this.provinceSelect.value = selectedCode;
      this.hint.hidden = !(canSelect && !this.provinceSelect.value);
      this.hint.textContent = canSelect ? 'Choose a state or province for a more precise estimate.' : '';
    }

    broadcast() {
      const option = this.countrySelect.options[this.countrySelect.selectedIndex];
      const detail = {
        country: this.countrySelect.value.toUpperCase(),
        countryName: option?.textContent?.trim() || this.countrySelect.value,
        province: this.provinceSelect.disabled ? '' : this.provinceSelect.value.toUpperCase(),
      };
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail }));
      this.render(detail);
    }

    render(destination = {}) {
      const { mapping, estimate } = this.mapping();
      const country = (destination.country || readStorage(COUNTRY_KEY) || window.OvilixaDeliveryData?.defaultCountry || 'US').toUpperCase();
      const countryName = destination.countryName || this.countrySelect.options[this.countrySelect.selectedIndex]?.textContent?.trim() || country;
      const province = (destination.province || readStorage(PROVINCE_KEY) || '').toUpperCase();
      const destinationData = estimate?.destinations?.[country];
      this.summary.classList.toggle('ov-product-delivery__summary--unavailable', !destinationData);
      if (!mapping || !estimate) {
        this.summary.innerHTML = '<strong>Delivery estimate unavailable.</strong><span>Please contact us before ordering.</span>';
        return;
      }
      if (!destinationData) {
        this.summary.innerHTML = `<strong>Not available for delivery to ${escape(countryName)}.</strong><span>Choose another destination to continue.</span>`;
        return;
      }
      const range = destinationData.provinces?.[province] || destinationData.fallback;
      const unit = estimate.dayUnit === 'business' ? 'business days' : 'days';
      const rangeText = range.min === range.max ? `${range.min} ${unit}` : `${range.min}–${range.max} ${unit}`;
      const needsProvince = destinationData.requiresProvince && !destinationData.provinces?.[province];
      const note = needsProvince
        ? 'Conservative range. Select a state or province for greater accuracy.'
        : estimate.includesProduction ? 'Preparation is included in this estimate.' : 'Estimated from dispatch.';
      this.summary.innerHTML = `<span class="ov-product-delivery__window-label">Your delivery window</span><div class="ov-product-delivery__window"><strong>Estimated delivery to ${escape(countryName)}</strong><span class="ov-product-delivery__range">${rangeText}</span></div><span class="ov-product-delivery__note">${note}</span>`;
    }
  }

  if (!customElements.get('ovilixa-product-delivery')) {
    customElements.define('ovilixa-product-delivery', OvilixaProductDelivery);
  }
})();
